import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, XCircle, Star, Trash2, BadgeCheck, LayoutDashboard, Flag, EyeOff, Eye, Globe } from "lucide-react";
import { AdminCommunityPanel } from "@/components/community/AdminCommunityPanel";
import { AdminCommunitiesTools } from "@/components/community/AdminCommunitiesTools";
import { AdminUsersList } from "@/components/admin/AdminUsersList";

const TABS = [
  { id: "reports", label: "Denúncias" },
  { id: "users", label: "Utilizadores" },
  { id: "businesses", label: "Negócios" },
  { id: "events", label: "Eventos" },
  { id: "jobs", label: "Empregos" },
  { id: "communities", label: "Comunidades" },
  { id: "marketplace", label: "Marketplace →" },
  { id: "featured", label: "Destaques →" },
  { id: "videos", label: "Vídeos →" },
  { id: "tickets", label: "Bilhetes →" },
  { id: "demo", label: "Demo" },
];

const REASON_LABELS = {
  off_platform_payment: "Tentativa de pagamento fora da plataforma",
  scam_fraud: "Fraude", harassment: "Assédio", hate_speech: "Ódio",
  illegal_content: "Ilegal", impersonation: "Falsa identidade",
  privacy_violation: "Privacidade", spam: "Spam",
  inappropriate: "Inapropriado", other: "Outro",
};

export default function Admin() {
  const { confirm, prompt } = useConfirm();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("reports");
  const [convView, setConvView] = useState(null);
  const openConversation = async (r) => {
    try {
      const cid = r.target_type === "conversation" ? r.target_id : (await api.get("/admin/messages/reports")).data.find(x => x.id === r.id)?.conversation_id;
      const { data } = await api.get(`/admin/messages/conversations/${cid}`, { params: { report_id: r.id } });
      setConvView({ ...data, report_id: r.id });
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível abrir a conversa"); }
  };
  const modMsg = async (mid, action) => {
    const reason = action === "hide" ? await prompt({ title: "Ocultar mensagem", description: "Motivo para ocultar:", required: true, confirmLabel: "Ocultar" }) : "x";
    if (!reason) return;
    try { await api.post(`/admin/messages/messages/${mid}/${action}`, action === "hide" ? { reason } : {}); toast.success(action === "hide" ? "Mensagem ocultada" : "Mensagem reposta"); const { data } = await api.get(`/admin/messages/conversations/${convView.conversation.id}`, { params: { report_id: convView.report_id } }); setConvView({ ...data, report_id: convView.report_id }); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const modConv = async (action, uid) => {
    const reason = await prompt({ title: action === "close" ? "Encerrar conversa" : "Bloquear mensagens", description: action === "close" ? "Motivo para encerrar a conversa:" : "Motivo para bloquear mensagens deste utilizador:", required: true, confirmLabel: action === "close" ? "Encerrar" : "Bloquear" });
    if (!reason) return;
    try { await api.post(action === "close" ? `/admin/messages/conversations/${convView.conversation.id}/close` : `/admin/users/${uid}/block-messaging`, { reason }); toast.success("Acção registada"); setConvView(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const [statusFilter, setStatusFilter] = useState("");
  const [items, setItems] = useState([]);
  const [demoSummary, setDemoSummary] = useState(null);

  const load = async () => {
    if (tab === "demo") {
      const s = await api.get("/admin/stats");
      setStats(s.data);
      const ds = await api.get("/admin/demo/summary");
      setDemoSummary(ds.data);
      setItems([]);
      return;
    }
    const endpoint = tab === "reports" ? "/admin/reports" : `/admin/${tab}`;
    const [s, l] = await Promise.all([
      api.get("/admin/stats"),
      api.get(endpoint, { params: statusFilter ? { status: statusFilter } : {} }),
    ]);
    setStats(s.data);
    setItems(l.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, statusFilter]);

  const doAction = async (id, action) => {
    try {
      const url = tab === "reports"
        ? `/admin/reports/${id}/action?action=${action}`
        : `/admin/${tab}/${id}/action?action=${action}`;
      await api.post(url);
      toast.success("Ação concluída");
      load();
    } catch (e) { toast.error("Erro na ação"); }
  };

  const toggleDemo = async (action) => {
    try {
      const { data } = await api.post(`/admin/demo/${action}`);
      toast.success(action === "hide" ? "Conteúdo demo ocultado" : "Conteúdo demo restaurado");
      load();
    } catch { toast.error("Erro"); }
  };

  const demoSeed = async (action) => {
    const path = action === "seed" ? "seed" : action === "sync" ? "sync" : "purge-seed";
    const label = action === "seed" ? "Inserir o conteúdo demo fictício (sellers, restaurantes, produtos, serviços, eventos)?" : action === "sync" ? "Actualizar nomes, descrições e imagens do conteúdo demo já inserido (sem duplicar, sem tocar em dados reais)?" : "Remover TODO o conteúdo demo fictício inserido pelo seed?";
    if (!(await confirm({ title: action === "purge" ? "Remover conteúdo demo" : action === "seed" ? "Inserir conteúdo demo" : "Actualizar conteúdo demo", description: label, confirmLabel: "Confirmar" }))) return;
    try {
      const { data } = await api.post(`/admin/demo/${path}`, { confirm: "SEED_DEMO_2026_10" });
      toast.success(data.status === "already_seeded" ? "Conteúdo demo já existia — nada inserido" : action === "seed" ? `Conteúdo demo inserido (${data.inserted} registos)` : action === "sync" ? `Conteúdo demo actualizado (${Object.values(data.modified).reduce((a, b) => a + b, 0)} registos)` : "Conteúdo demo removido");
      load();
    } catch { toast.error("Erro"); }
  };

  const createGeneral = async () => {
    try {
      const { data } = await api.post("/admin/communities/general");
      toast.success(data.created ? "Comunidade Geral criada" : "Comunidade Geral já existia — nada alterado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro ao criar Comunidade Geral"); }
  };

  const backfillGeneral = async () => {
    try {
      const { data: dry } = await api.post("/admin/communities/general/backfill", { dry_run: true });
      const ok = await confirm({ title: "Adicionar membros existentes à Comunidade Geral", description: `${dry.would_add} utilizador(es) seriam adicionados · ${dry.skipped_existing} já têm registo (membro, saiu ou banido) e não são tocados. Esta ação é irreversível em massa. Executar agora?`, confirmLabel: `Adicionar ${dry.would_add}` });
      if (!ok) return toast.info(`Simulação: ${dry.would_add} seriam adicionados · nada foi alterado`);
      const { data } = await api.post("/admin/communities/general/backfill", { dry_run: false, confirm: "BACKFILL_GENERAL" });
      toast.success(`${data.added} membro(s) adicionados à Comunidade Geral`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro na adesão em massa"); }
  };

  const createOfficial = async () => {
    try {
      const { data } = await api.post("/admin/communities/official-cities", {});
      toast.success(`${data.created.length} criadas · ${data.existing.length} já existentes`);
      load();
    } catch { toast.error("Erro ao criar comunidades oficiais"); }
  };

  const applyOfficialContent = async () => {
    try {
      const { data } = await api.post("/admin/communities/official-content", {});
      const s = data.summary;
      toast.success(`Conteúdo oficial: ${s.description} descrições · ${s.rules} regras · ${s.welcome_post} boas-vindas · ${s.pinned} fixados (restantes já preparados/personalizados)`);
      load();
    } catch { toast.error("Erro ao preparar conteúdo oficial"); }
  };

  return (
    <div className="page fade-up">
      {convView && <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setConvView(null)}>
        <div data-testid="admin-conversation-view" onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl">
          <div className="p-3 border-b border-zinc-200 text-xs"><div className="font-semibold text-sm">Conversa denunciada</div><div className="text-zinc-500">{convView.conversation.context?.snapshot?.title} · {Object.values(convView.participants).join(" ↔ ")} · estado {convView.conversation.status}</div><div className="text-[10px] text-orange-700 mt-1">Acesso registado em admin_access_log (denúncia {convView.report_id.slice(0, 8)}…)</div></div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5 text-xs">
            {[...convView.messages].reverse().map(m => <div key={m.id} data-testid={`admin-msg-${m.id}`} className="bg-zinc-50 border border-zinc-200 rounded-xl p-2">
              <div className="flex justify-between text-[10px] text-zinc-500"><span>{m.sender_name}</span><span>{new Date(m.created_at).toLocaleString("pt-PT")} · {m.status}</span></div>
              <p className="mt-0.5 whitespace-pre-wrap">{m.text}</p>
              <div className="flex gap-1 mt-1">{m.status === "sent" && <ActionBtn onClick={() => modMsg(m.id, "hide")} icon={EyeOff} label="Ocultar" tone="red" />}{m.status === "hidden_by_admin" && <ActionBtn onClick={() => modMsg(m.id, "unhide")} icon={Eye} label="Repor" tone="green" />}</div>
            </div>)}
          </div>
          <div className="p-3 border-t border-zinc-200 flex flex-wrap gap-1">
            {convView.conversation.status === "active" && <ActionBtn onClick={() => modConv("close")} icon={XCircle} label="Encerrar conversa" tone="red" />}
            {convView.conversation.participants.map(uid => <ActionBtn key={uid} onClick={() => modConv("block", uid)} icon={Flag} label={`Bloquear ${convView.participants[uid] || "utilizador"}`} tone="yellow" />)}
            <button onClick={() => setConvView(null)} className="ml-auto text-xs underline">Fechar</button>
          </div>
        </div>
      </div>}
      <div className="pt-4 pb-4 flex items-center gap-2">
        <LayoutDashboard className="text-[#c8102e]" size={22} />
        <h1 className="font-display text-2xl font-extrabold">Admin</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="Utilizadores" val={stats.users} onClick={() => { setTab("users"); setStatusFilter(""); }} testId="admin-stat-users" />
          <Stat label="Negócios" val={stats.businesses} pending={stats.businesses_pending} />
          <Stat label="Eventos" val={stats.events} pending={stats.events_pending} />
          <Stat label="Empregos" val={stats.jobs} />
          <Stat label="Comunidades" val={stats.communities} />
        </div>
      )}

      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-2">
        {TABS.map(x => (
          <button key={x.id} onClick={() => { if (x.id === "marketplace") return nav("/admin/marketplace"); if (x.id === "tickets") return nav("/admin/bilhetes"); if (x.id === "featured") return nav("/admin/destaques"); if (x.id === "videos") return nav("/admin/videos"); setTab(x.id); setStatusFilter(""); }} data-testid={`admin-tab-${x.id}`}
                  className="chip whitespace-nowrap" data-active={tab === x.id}>
            {x.label}
          </button>
        ))}
      </div>

      {tab !== "demo" && tab !== "users" && (
        <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-4">
          {(tab === "reports"
            ? ["", "open", "resolved", "dismissed"]
            : ["", "pending", "approved", "rejected", "hidden"]
          ).map(s => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)}
                    className="chip whitespace-nowrap" data-active={statusFilter === s}>
              {s ? s : "Todos"}
            </button>
          ))}
        </div>
      )}

      {tab === "communities" && <AdminCommunitiesTools />}
      {tab === "communities" && (
        <div className="mb-3 bg-[#0d0d0d] text-white rounded-2xl p-3 space-y-2" data-testid="admin-general-community-panel">
          <div className="text-xs font-semibold flex items-center gap-1"><Globe size={13} /> Comunidade Geral (nacional · default para novos registos)</div>
          <div className="flex gap-2">
            <button data-testid="admin-create-general" onClick={createGeneral} className="flex-1 py-2 rounded-xl bg-[#c8102e] text-xs font-semibold hover:bg-[#a50c24] transition-colors">Criar / verificar Comunidade Geral</button>
            <button data-testid="admin-backfill-general" onClick={backfillGeneral} className="flex-1 py-2 rounded-xl border border-white/30 text-xs font-semibold hover:border-white transition-colors">Membros existentes → simular / adicionar</button>
          </div>
          <p className="text-[10px] text-white/50 leading-relaxed">Novos registos entram automaticamente (1 membership, sem duplicados). Quem sair não volta a ser adicionado. A adesão em massa mostra primeiro uma simulação e só executa após confirmação.</p>
        </div>
      )}
      {tab === "communities" && (
        <button data-testid="admin-create-official-cities" onClick={createOfficial}
                className="w-full mb-3 flex items-center justify-center gap-2 py-3 rounded-xl bg-black text-white text-sm font-semibold hover:bg-[#c8102e] transition-colors">
          <BadgeCheck size={14} /> Criar comunidades oficiais das cidades em falta
        </button>
      )}
      {tab === "communities" && (
        <button data-testid="admin-apply-official-content" onClick={applyOfficialContent}
                className="w-full mb-3 flex items-center justify-center gap-2 py-3 rounded-xl border border-zinc-300 text-sm font-semibold hover:border-black transition-colors">
          <Star size={14} /> Preparar conteúdo das comunidades oficiais (descrição, regras, boas-vindas)
        </button>
      )}

      {tab === "demo" && demoSummary && (
        <div className="space-y-3 mb-8">
          <div className="bg-yellow-50 border border-yellow-300 rounded-2xl p-4">
            <p className="text-xs text-yellow-900 leading-relaxed">
              Conteúdo demo é conteúdo de teste flagged com <code>is_demo=true</code>. Podes ocultá-lo antes do lançamento público — a operação é <strong>não-destrutiva</strong> e reversível.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(demoSummary).map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl border border-zinc-200 p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
                <div className="font-display font-bold text-lg">{v.total}</div>
                <div className="text-[10px] text-zinc-500">
                  {v.visible} visível · {v.hidden} oculto
                </div>
              </div>
            ))}
          </div>
          <button data-testid="admin-demo-hide" onClick={() => toggleDemo("hide")}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-black text-white text-sm font-semibold hover:bg-[#c8102e] transition-colors">
            <EyeOff size={14} /> Ocultar todo o conteúdo demo
          </button>
          <button data-testid="admin-demo-show" onClick={() => toggleDemo("show")}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-zinc-200 text-sm font-semibold hover:border-black transition-colors">
            <Eye size={14} /> Restaurar conteúdo demo oculto
          </button>
          <div className="pt-3 border-t border-zinc-200 space-y-2">
            <p className="text-xs text-zinc-500">Conteúdo demo fictício (seed 2026-10): 9 vendedores, 4 restaurantes com menu, 6 produtos, 5 serviços, 4 eventos. Idempotente — não duplica.</p>
            <button data-testid="admin-demo-seed" onClick={() => demoSeed("seed")}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-black text-white text-sm font-semibold hover:bg-[#c8102e] transition-colors">
              Inserir conteúdo demo fictício
            </button>
            <button data-testid="admin-demo-sync" onClick={() => demoSeed("sync")}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-zinc-300 text-sm font-semibold hover:border-black transition-colors">
              Actualizar conteúdo demo existente
            </button>
            <button data-testid="admin-demo-purge" onClick={() => demoSeed("purge")}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:border-red-600 transition-colors">
              Remover conteúdo demo fictício
            </button>
          </div>
        </div>
      )}

      {tab === "reports" && (
        <div className="space-y-2 mb-8">
          {items.length === 0 && <p className="text-sm text-zinc-500 italic text-center py-8">Sem denúncias.</p>}
          {items.map(r => (
            <div key={r.id} data-testid={`admin-report-${r.id}`} className="bg-white rounded-2xl border border-zinc-200 p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 font-semibold text-sm">
                    <Flag size={12} className="text-[#c8102e]" />
                    {REASON_LABELS[r.reason] || r.reason}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {r.target_type} · <span className="font-mono">{r.target_id.slice(0, 8)}…</span>
                  </div>
                  {r.notes && <p className="text-xs text-zinc-700 mt-1 leading-relaxed">"{r.notes}"</p>}
                  <div className="text-[10px] text-zinc-400 mt-1">
                    por {r.reporter_email} · {new Date(r.created_at).toLocaleString("pt-PT")}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {["message", "conversation"].includes(r.target_type) && <ActionBtn onClick={() => openConversation(r)} icon={Eye} label="Ver conversa" tone="zinc" />}
                {r.status === "open" && <>
                  <ActionBtn onClick={() => doAction(r.id, "remove_content")} icon={Trash2} label="Remover conteúdo" tone="red" />
                  <ActionBtn onClick={() => doAction(r.id, "resolve")} icon={CheckCircle2} label="Resolver" tone="green" />
                  <ActionBtn onClick={() => doAction(r.id, "dismiss")} icon={XCircle} label="Ignorar" tone="zinc" />
                </>}
                {r.status !== "open" && (
                  <ActionBtn onClick={() => doAction(r.id, "reopen")} icon={Flag} label="Reabrir" tone="yellow" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && <AdminUsersList users={items} />}

      {tab !== "reports" && tab !== "demo" && tab !== "users" && (
        <div className="space-y-2 mb-8">
          {items.length === 0 && <p className="text-sm text-zinc-500 italic text-center py-8">Sem itens.</p>}
          {items.map(it => (
            <div key={it.id} className="bg-white rounded-2xl border border-zinc-200 p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-1">
                    {it.name || it.title}
                    {it.verified && <BadgeCheck size={13} className="text-[#c8102e]" />}
                    {it.featured && <Star size={12} className="text-[#ffd100]" fill="currentColor" />}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">{it.city} · {it.category || ""}</div>
                  <StatusBadge status={it.status} />
                  {it.is_demo && <span className="ml-1 demo-badge">DEMO</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {it.status !== "approved" && <ActionBtn data-testid={`admin-approve-${it.id}`} onClick={() => doAction(it.id, "approve")} icon={CheckCircle2} label="Aprovar" tone="green" />}
                {it.status !== "rejected" && <ActionBtn onClick={() => doAction(it.id, "reject")} icon={XCircle} label="Rejeitar" tone="zinc" />}
                {(tab === "businesses" || tab === "events") && (
                  <ActionBtn onClick={() => doAction(it.id, it.featured ? "unfeature" : "feature")} icon={Star} label={it.featured ? "Remover destaque" : "Destacar"} tone="yellow" />
                )}
                {tab === "businesses" && !it.verified && (
                  <ActionBtn onClick={() => doAction(it.id, "verify")} icon={BadgeCheck} label="Verificar" tone="red" />
                )}
                {!(tab === "communities" && it.official) && <ActionBtn onClick={() => doAction(it.id, "delete")} icon={Trash2} label="Apagar" tone="red" />}
              </div>
              {tab === "communities" && <AdminCommunityPanel c={it} onChange={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, val, pending, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag data-testid={testId} onClick={onClick} className={`bg-white rounded-xl border border-zinc-200 p-3 text-left ${onClick ? "hover:border-black transition-colors cursor-pointer" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-display font-bold text-xl">{val}</div>
      {onClick && <div className="text-[10px] text-zinc-500 font-semibold">Ver lista →</div>}
      {pending > 0 && <div className="text-[10px] text-[#c8102e] font-semibold">{pending} pendente(s)</div>}
    </Tag>
  );
}
function StatusBadge({ status }) {
  const cfg = {
    approved: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-800",
    rejected: "bg-red-100 text-red-700",
    hidden: "bg-zinc-200 text-zinc-700",
    suspended: "bg-orange-100 text-orange-800",
    removed: "bg-zinc-200 text-zinc-700",
    open: "bg-orange-100 text-orange-800",
    resolved: "bg-green-100 text-green-700",
    dismissed: "bg-zinc-100 text-zinc-600",
  };
  return <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${cfg[status] || "bg-zinc-100"}`}>{status}</span>;
}
function ActionBtn({ onClick, icon: I, label, tone = "zinc", ...rest }) {
  const tones = {
    green: "bg-green-600 hover:bg-green-700 text-white",
    yellow: "bg-yellow-500 hover:bg-yellow-600 text-black",
    red: "bg-[#c8102e] hover:bg-[#a50c24] text-white",
    zinc: "bg-zinc-800 hover:bg-black text-white",
  };
  return (
    <button {...rest} onClick={onClick} className={`flex items-center gap-1 text-[11px] font-semibold px-2 py-1.5 rounded-lg transition-colors ${tones[tone]}`}>
      <I size={11} /> {label}
    </button>
  );
}
